import { useCallback, useEffect, useRef, useState } from 'react';
import { format, startOfDay, endOfDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';
import { GoogleEvent } from '../types/calendar.types';
import { APP_ICON } from '../lib/brand';

const AUDIO_PATH = '/sounds/reminder.mp3';
const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // ricarica gli eventi ogni 5 minuti

interface RawEvent {
  id: string;
  title: string;
  location?: string | null;
  startDate: string;
  endDate: string;
  allDay: boolean;
}

// Allarme audio + notifica allo START di ogni evento del Google Calendar.
// Funziona finché l'app è aperta in una scheda del browser.
export const useAppointmentReminders = () => {
  const { addNotification } = useNotifications();
  const [events, setEvents] = useState<GoogleEvent[]>([]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timersRef = useRef<number[]>([]);

  // Prepara l'audio una volta sola
  useEffect(() => {
    const audio = new Audio(AUDIO_PATH);
    audio.preload = 'auto';
    audioRef.current = audio;
  }, []);

  // Sblocca l'audio al primo gesto utente (policy autoplay dei browser)
  useEffect(() => {
    const unlock = () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.play().then(() => {
        audio.pause();
        audio.currentTime = 0;
      }).catch(() => { /* riproveremo al trigger */ });
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // Chiede il permesso notifiche una volta
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => { /* ignorato */ });
    }
  }, []);

  // Carica gli eventi di oggi dal Google Calendar (fetch indipendente dallo store)
  useEffect(() => {
    let cancelled = false;

    const loadToday = async () => {
      const now = new Date();
      try {
        const { data, error } = await supabase.functions.invoke('google-calendar-events', {
          body: { timeMin: startOfDay(now).toISOString(), timeMax: endOfDay(now).toISOString() },
        });
        if (error || !data?.ok) return; // niente allarmi se la function fallisce o non è configurata
        if (cancelled) return;
        const mapped = ((data.events ?? []) as RawEvent[]).map((r) => ({
          id: r.id,
          title: r.title,
          location: r.location || undefined,
          startDate: new Date(r.startDate),
          endDate: new Date(r.endDate),
          allDay: r.allDay,
        })) as GoogleEvent[];
        setEvents(mapped);
      } catch {
        /* ignorato */
      }
    };

    loadToday();
    const refreshId = window.setInterval(loadToday, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(refreshId);
    };
  }, []);

  const triggerReminder = useCallback(
    (event: GoogleEvent) => {
      const when = format(new Date(event.startDate), 'HH:mm', { locale: it });
      const message = [event.title, event.location ? `• ${event.location}` : null, `(${when})`]
        .filter(Boolean)
        .join(' ');

      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => { /* gesto utente richiesto */ });
      }

      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Appuntamento in corso', { body: message, icon: APP_ICON });
      }

      addNotification({ type: 'warning', title: 'Appuntamento in corso', message });
    },
    [addNotification]
  );

  // Programma un timer allo start di ogni evento futuro di oggi
  useEffect(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];

    if (!events.length) return;

    events.forEach((event) => {
      if (event.allDay) return; // niente orario preciso → niente allarme audio
      const startTime = new Date(event.startDate).getTime();
      if (!startTime) return;

      const delay = startTime - Date.now();
      if (delay <= 0) return; // evento già passato → non suona (evita refire dopo i refresh)

      const timeoutId = window.setTimeout(() => triggerReminder(event), delay);
      timersRef.current.push(timeoutId);
    });

    return () => {
      timersRef.current.forEach((id) => window.clearTimeout(id));
      timersRef.current = [];
    };
  }, [events, triggerReminder]);
};
